import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Clock, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const UPI_ID = "9810777489@ibl";
const BUSINESS_NAME = "PLAYEDGE INNOVATIONS (Fokatmain)";

const UPIPaymentModal = ({ isOpen, onClose, gameName, gameId, amount = 10, onPaymentSuccess }) => {
  const [transactionId, setTransactionId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect if user is on mobile
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
  }, []);

  const copyUPIId = () => {
    navigator.clipboard.writeText(UPI_ID);
    toast.success('UPI ID copied!');
  };

  const openUPIApp = () => {
    // UPI deep link format
    const upiLink = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(BUSINESS_NAME)}&am=${amount}&tn=${encodeURIComponent(`Payment for ${gameName}`)}&cu=INR`;
    window.location.href = upiLink;
    toast.info('Opening UPI app...');
  };

  const handleSubmit = async () => {
    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }

    if (transactionId.trim().length < 6) {
      toast.error('Please enter a valid transaction ID');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('fokatmain_user') || '{}');
      
      if (!userData.id) {
        toast.error('Please login first');
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post(`${BACKEND_URL}/api/payments/upi/submit`, {
        user_id: userData.id,
        user_name: userData.name,
        user_email: userData.email,
        game_id: gameId,
        game_name: gameName,
        amount: amount,
        upi_transaction_id: transactionId.trim()
      });

      if (response.data.success) {
        const allowedAttempts = response.data.allowed_attempts || 1;
        const trustScore = response.data.trust_score || 'new';
        
        // Show appropriate message based on trust score
        if (trustScore === 'high') {
          toast.success('✅ Payment verified! You have full access with 3 attempts.');
        } else if (trustScore === 'medium') {
          toast.success(`✅ ${response.data.message}`);
        } else {
          toast.info(
            `⏳ Payment submitted for verification. You can play with 1 trial attempt now. Full 3 attempts unlocked after admin approval (usually within 24 hours).`,
            { duration: 6000 }
          );
        }
        
        // Call success callback first
        if (onPaymentSuccess) {
          onPaymentSuccess({
            payment_id: response.data.payment_id,
            status: response.data.status,
            allowed_attempts: allowedAttempts,
            trust_score: trustScore
          });
        }
        
        // Close modal after callback
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit payment. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-fade-in-up my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Pay to Play</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Game Info */}
        <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Game</p>
              <p className="text-white font-semibold">{gameName}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-sm">Amount</p>
              <p className="text-yellow-400 font-bold text-xl">₹{amount}</p>
            </div>
          </div>
        </div>

        {/* Mobile - UPI Link Button */}
        {isMobile && (
          <div className="mb-4">
            <button
              onClick={openUPIApp}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 text-black font-bold py-4 rounded-xl hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
            >
              <Smartphone className="w-5 h-5" />
              <span>Pay ₹10 with UPI App</span>
            </button>
            <p className="text-gray-400 text-xs text-center mt-2">
              Opens PhonePe, GPay, Paytm or any UPI app
            </p>
            
            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-900 px-2 text-gray-400">Or Scan QR Code</span>
              </div>
            </div>
          </div>
        )}

        {/* QR Code - Show on Both Mobile & Desktop */}
        <div className="mb-6">
          <div className="bg-white p-4 rounded-xl mx-auto" style={{maxWidth: isMobile ? '250px' : '200px'}}>
            <img
              src="/phonepe_qr.jpeg"
              alt="PhonePe QR Code"
              className="w-full h-auto rounded-lg"
              style={{maxHeight: isMobile ? '250px' : '200px', objectFit: 'contain'}}
            />
          </div>
          <p className="text-gray-400 text-sm text-center mt-3">
            Scan with PhonePe, GPay, Paytm or any UPI app
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-2">
            <Clock className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-400 font-semibold text-sm mb-1">Smart Verification System:</p>
              <p className="text-gray-300 text-sm mb-2">
                Enter your UPI transaction ID below. Our system validates:
              </p>
              <ul className="text-gray-400 text-xs space-y-1 ml-2">
                <li>• 12-digit format check</li>
                <li>• Duplicate transaction prevention</li>
                <li>• Your payment history & trust score</li>
              </ul>
              <p className="text-yellow-400 text-xs mt-2 font-semibold">
                ⚡ New users: 1 trial attempt. Regular users: Full 3 attempts after admin approval!
              </p>
            </div>
          </div>
        </div>

        {/* Transaction ID Input */}
        <div className="mb-6">
          <label className="block text-gray-300 text-sm font-semibold mb-2">
            Transaction ID / UPI Reference Number
          </label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter 12-digit transaction ID"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 outline-none transition-all"
          />
          <p className="text-gray-500 text-xs mt-1">
            Found in payment confirmation SMS or UPI app
          </p>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 mb-3"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              <span>Submit & Start Playing</span>
            </>
          )}
        </button>

        {/* Close/Cancel Button */}
        <button
          onClick={onClose}
          className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-all duration-300"
        >
          Close / Cancel
        </button>

        {/* Trust Badge */}
        <div className="mt-4 text-center">
          <p className="text-gray-500 text-xs">
            🔒 Secure payment • Your money is safe • Instant verification
          </p>
        </div>
      </div>
    </div>
  );
};

export default UPIPaymentModal;
